/* ------------------------------------------------------------------ */
/* Romanian Experiences — single source of truth.                      */
/* Used by:                                                            */
/*   • Homepage "Romanian Experiences" section                         */
/*     (src/components/zera/romania/RomanianExperiencesSection.tsx)    */
/*   • Full experiences page (src/pages/romania/RomaniaExperiences.tsx)*/
/* "Learn More" deep-links to /romania/experiences#<slug>.             */
/* ------------------------------------------------------------------ */

export interface RomanianExperience {
  slug: string;
  title: string;
  tagline: string;
  /** One-sentence teaser shown on the homepage cards. */
  description: string;
  image: string;
  alt: string;
  /** Wider hero-style card in the homepage mosaic grid. */
  featured?: boolean;
}

export const romaniaExperiencesIntro =
  'Romania is more than a collection of places — it is a country you feel. ' +
  'Go beyond the map and discover the adventures, flavours, legends and rituals ' +
  'that turn a journey through Romania into a story you will tell forever.';

export const romanianExperiences: RomanianExperience[] = [
  {
    slug: 'luxury-lifestyle',
    title: 'Luxury Lifestyle',
    tagline: 'Castles, Concierge & VIP Hospitality',
    description:
      "Experience Romania's finest luxury hotels, castles, private tours, gourmet dining, exclusive experiences, and VIP hospitality.",
    image:
      'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1783694540349_54306290.jpg',
    alt: 'Opulent Romanian castle hotel terrace at golden hour with champagne service, luxury travel in Romania.',
    featured: true,
  },
  {
    slug: 'dracula-transylvania',
    title: 'Dracula & Transylvania',
    tagline: 'Legends, Castles & Gothic Wonders',
    description:
      'Step into the legends of Dracula through medieval castles, Gothic architecture, historic villages, and unforgettable photography opportunities.',
    image:
      'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1783694558958_1c6c39ed.jpg',
    alt: 'Bran Castle at dusk shrouded in mist, the gothic Transylvanian fortress at the heart of the Dracula legend.',
    featured: true,
  },
  {
    slug: 'hunting-adventures',
    title: 'Hunting Adventures',
    tagline: 'Guided Expeditions & Mountain Lodges',
    description:
      "Discover professionally guided hunting expeditions through Romania's pristine forests while experiencing authentic mountain lodges and local traditions.",
    image:
      'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1783694588838_96a69519.png',
    alt: 'Hunter and hunting dog walking through a misty autumn Carpathian forest at dawn in Romania.',
  },
  {
    slug: 'horse-riding',
    title: 'Horse Riding',
    tagline: 'Countryside Trails on Horseback',
    description:
      'Ride through picturesque villages, rolling countryside, forests, vineyards, and scenic mountain trails on guided horseback adventures.',
    image:
      'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1783694620917_5b0de988.png',
    alt: 'Horseback rider on a scenic trail through rolling Transylvanian countryside with a village in the distance.',
  },
  {
    slug: 'baths-spa-retreats',
    title: 'Romanian Baths & Spa Retreats',
    tagline: 'Thermal Springs & Wellness',
    description:
      "Relax in Romania's renowned thermal spas, wellness resorts, mineral springs, and rejuvenating luxury retreats.",
    image:
      'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1783694653488_bc9d0fbe.png',
    alt: 'Elegant historic thermal bathhouse in Romania with steaming turquoise pool and art-nouveau architecture.',
  },
  {
    slug: 'craft-culture-culinary',
    title: 'Craft, Culture & Culinary',
    tagline: 'Artisans, Villages & Flavours',
    description:
      'Learn traditional crafts, enjoy authentic Romanian cuisine, meet local artisans, explore village life, and participate in hands-on cultural experiences.',
    image:
      'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1783694672752_fc460868.jpg',
    alt: "Romanian artisan's hands painting traditional folk pottery in a rustic village workshop.",
  },
  {
    slug: 'hiking-biking',
    title: 'Mountain Hiking & Biking',
    tagline: 'Carpathian Trails & Panoramas',
    description:
      'Explore the breathtaking Carpathian Mountains with guided hikes, mountain biking trails, alpine scenery, waterfalls, and panoramic viewpoints.',
    image:
      'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1783694697395_dbf310a6.png',
    alt: 'Hiker on a Carpathian mountain ridge overlooking dramatic alpine peaks and valleys in Romania.',
  },
  {
    slug: 'bear-watching',
    title: 'Bear Watching',
    tagline: "Europe's Wild Heart",
    description:
      "Observe Europe's largest population of wild brown bears from safe viewing locations while learning about Romania's incredible wildlife.",
    image:
      'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1783694727464_b77d32f1.png',
    alt: 'Wild brown bear in a sunlit Carpathian forest clearing in Romania.',
  },
  {
    slug: 'ski-adventures',
    title: 'Ski Adventures',
    tagline: 'Alpine Winters & Cozy Lodges',
    description:
      "Experience Romania's winter resorts with skiing, snowboarding, cozy mountain lodges, and unforgettable alpine scenery.",
    image:
      'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1783694755818_aef2aae6.png',
    alt: 'Skier carving through fresh powder on a sunny Carpathian slope with a mountain lodge below.',
  },
  {
    slug: 'vineyard-wine-tours',
    title: 'Vineyard & Wine Tours',
    tagline: 'Cellars, Tastings & Vines',
    description:
      'Visit historic vineyards, taste award-winning Romanian wines, enjoy private cellar tours, gourmet pairings, and scenic countryside escapes.',
    image:
      'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1783694772023_5fbfde68.jpg',
    alt: 'Romanian hillside vineyard at golden hour with glasses of red wine on a rustic table.',
  },
];
